/** For details on how the events should be used, see {@link https://iodigitalcom.notion.site/Analytics-241d4e5d405c819d8cc2c6a0b45d7366?p=241d4e5d405c817fbb16cae5ea8ba559&pm=s | the documentation} */

export type AnalyticsSection = 'nav' | 'body' | 'modal'

export enum AnalyticsEventType {
    CONTENT_INTERACTION = 'content_interaction',
    NAVIGATION = 'navigation',
    FORM_START = 'form_start',
    FORM_PROGRESS = 'form_progress',
    FORM_COMPLETE = 'form_complete',
    FORM_INTERACTION = 'form_interaction',
    FORM_ERROR = 'form_error',
    LOGIN = 'login',
    LOGOUT = 'logout',
    ADD_TO_CART = 'add_to_cart',
    REMOVE_FROM_CART = 'remove_from_cart',
    SEARCH = 'search',
}

export type AnalyticsEvent =
    | LogoutAnalyticsEvent
    | AddToCartAnalyticsEvent
    | RemoveFromCartAnalyticsEvent
    | FormStartAnalyticsEvent
    | FormProgressAnalyticsEvent
    | FormCompleteAnalyticsEvent
    | SearchAnalyticsEvent
    | ContentInteractionAnalyticsEvent
    | NavigationAnalyticsEvent
    | FormErrorAnalyticsEvent
    | FormInteractionAnalyticsEvent

export interface ContentInteractionAnalyticsEvent {
    event: AnalyticsEventType.CONTENT_INTERACTION
    /**
     * @todo implement this. not implemented yet.
     * @note can only be achieved by adding new information to the DOM. very interesting information but also high effort.
     */
    interaction_type: 'click' | 'open' | 'close' | 'next' | 'previous' | 'change'
    /**
     * @description Self-defined section types such as: header | navigation | modal
     * @deprecated not MVP
     **/
    tm_type: 'header' | 'navigation' | 'modal' | ''
    /**
     * @description Inferred from DOM: navigation landmark, main page content, or modal layer.
     **/
    tm_section: AnalyticsSection
    /** @description Self-defined element types such as:
     *  button_primary | button_secondary | button_ghost | text_link | product_card | image
     **/
    tm_element:
        | 'button_primary'
        | 'button_secondary'
        | 'button_quiet'
        | 'button_tertiary'
        | 'danger_primary'
        | 'danger_quiet'
        | 'danger_secondary'
        | 'text_link'
        | ''
    /**
     * @description The literal text that is clicked on or is shown on the button or the title of the image that was clicked
     **/
    link_text: string
    /**
     * @description The URL of the link that was clicked
     * @note should always be empty as we are not navigating. the exception to the rule is when the link is an anchor link.
     * @todo implement 'anchor' edge case.
     **/
    link_url: string
}

export interface NavigationAnalyticsEvent {
    event: AnalyticsEventType.NAVIGATION
    /**
     * @note not implemented. not currently in MVP scope. can only be achieved by adding new information to the DOM.
     */
    interaction_type: 'click' | 'open' | 'close' | 'next' | 'previous'
    /**
     * @description Inferred from DOM: navigation landmark, main page content, or modal layer.
     **/
    tm_section: AnalyticsSection
    /**
     * @note not implemented yet. not MVP
     * @description component name or type within the section, as also shown in the HTML of the page. is more specific than 'tm_section' ('header' would be section, 'header-nav' would be component)
     **/
    tm_component: string
    /**
     * @description position of the component within grid or list
     * @deprecated will never be implemented. Not interesting for application.
     **/
    tm_index: string
    /**
     * @description element name or type within component, as also shown in HTML of the page
     **/
    tm_element: 'link'
    /** @description The literal text that is clicked on or is shown on the button or the title of the image that was clicked */
    link_text: string
    /** @description The URL of the link that was clicked */
    link_url: string
    /**
     * @description ONLY APPLICABLE ON SEARCH RESULTS PAGE; the term that was searched for
     * @note not implemented yet. not currently in mvp scope. could be achievable by reading url.
     **/
    search_term: string
}

export interface LogoutAnalyticsEvent {
    event: AnalyticsEventType.LOGOUT
    tm_status: 'success' | 'fail'
    /** The text that is shown to the user when te login attempt fails */
    tm_value: string
    /** Number of logout attempts in a session */
    tm_count: string
}

export interface AnalyticsCartItem {
    /**
     * Mandatory
     * Descriptive name of the item
     */
    item_name: string
    /**
     * Mandatory
     * Unique id of the item, often SKU
     */
    item_id: string
    /** Price of the item, shipping and tax excluded */
    price: string
    /** Brand of the item */
    item_brand: string
    /** The top-level category of the item */
    item_category: string
    /** The second level category of the item */
    item_category2: string
    /** The third level category of the item */
    item_category3: string
    /** The fourth level category of the item */
    item_category4: string
    /** The fifth level category of the item */
    item_category5: string
    /** Item variant name or description */
    item_variant: string
    /** The list from which the item was selected */
    item_list_name: string
    /** The id of the list from which the item was selected */
    item_list_id: string
    /** Quantity of the item. Must be an integer */
    quantity: string
    /** List of active filters when the product is added to cart */
    tm_active_filters: string
}

export interface AddToCartAnalyticsEvent {
    event: AnalyticsEventType.ADD_TO_CART
    ecommerce: {
        items: AnalyticsCartItem[]
    }
}

export interface RemoveFromCartAnalyticsEvent {
    event: AnalyticsEventType.REMOVE_FROM_CART
    ecommerce: {
        items: AnalyticsCartItem[]
    }
}

/**
 * do send if starting a new form
 * do send if starting a form from a stored state (like statements)
 * do NOT send if progressing to next step
 */
export interface FormStartAnalyticsEvent {
    event: AnalyticsEventType.FORM_START
    /**
     * Mandatory
     * Descriptive name of the form
     */
    form_name: string
    /**
     * Unique ID of the form
     *
     * move this data to form_name and leave `form_id` empty
     *
     * @deprecated form id would be a generated id like if we made forms with a CMS. not applicable for our forms, so leave empty.
     */
    form_id: ''
    /**
     * Mandatory
     * Form step index
     */
    tm_index: string
    /**
     * Mandatory
     * Form step name
     */
    tm_name: string
}

export interface FormProgressAnalyticsEvent {
    event: AnalyticsEventType.FORM_PROGRESS
    /**
     * Mandatory
     * Descriptive name of the form
     */
    form_name: string
    /**
     * Mandatory
     * Unique ID of the form
     *
     * move this data to form_name and leave `form_id` empty
     *
     * @deprecated
     */
    form_id: ''
    /**
     * Mandatory
     * Form step index
     */
    tm_index: string
    /**
     * Mandatory
     * Form step name
     */
    tm_name: string
    /** The number of attempts to finish this form_step */
    count: string
}

export interface FormCompleteAnalyticsEvent {
    event: AnalyticsEventType.FORM_COMPLETE
    /**
     * Mandatory
     * Descriptive name of the form
     */
    form_name: string
    /**
     * Mandatory
     * Unique ID of the form
     *
     * move this data to form_name and leave `form_id` empty
     *
     * @deprecated
     */
    form_id: ''
    /**
     * Mandatory
     * Form step index
     */
    tm_index: string
    /**
     * Mandatory
     * Form step name
     * When only one step, the form step name is the form name
     */
    tm_name: string
    /** The number of attempts to finish this form_step */
    count: string
}

export interface SearchAnalyticsEvent {
    event: AnalyticsEventType.SEARCH
    /**
     * Mandatory
     */
    search_term: string
    /**
     * Mandatory
     * @description List of active filters applied to the search results
     * @note not MVP
     * @todo implement this. not implemented yet.
     */
    tm_active_filters: ''
    /**
     * The number of results shown after search
     * NOTE: not interesting for now
     */
    tm_count: ''
}

export interface FormErrorAnalyticsEvent {
    event: AnalyticsEventType.FORM_ERROR
    /**
     * Mandatory
     * Descriptive name of the form
     */
    form_name: string
    /**
     * Mandatory
     * Unique ID of the form
     *
     * move this data to form_name and leave `form_id` empty
     *
     * @deprecated
     */
    form_id: ''
    /**
     * Mandatory
     * Form step index
     */
    tm_index: string
    /**
     * Mandatory
     * element name or type within component, as also shown in HTML of the page
     */
    tm_name: string
    /**
     * The names of the fields that had validation errors comma separated
     */
    tm_field_name: string
    /**
     * Mandatory
     * Shown errors to user comma separated
     */
    tm_value: string
    /**
     * The error type: formfieldError | formError
     */
    tm_type: 'formfieldError' | 'formError'
    /**
     * The number of attempts to proceed to next step with an error
     */
    tm_count: string
}

export interface FormInteractionAnalyticsEvent {
    event: AnalyticsEventType.FORM_INTERACTION
    /**
     * Mandatory
     * Descriptive name of the form
     */
    form_name: string
    /**
     * Mandatory
     * Unique ID of the form
     *
     * move this data to form_name and leave `form_id` empty
     *
     * @deprecated
     */
    form_id: ''
    /**
     * Mandatory
     * Form step index
     */
    tm_index: string
    /**
     * Mandatory
     * Form step name
     */
    tm_name: string
    /**
     * Form field name
     */
    tm_field_name?: string
    /**
     * Value that was set after interaction (not for text fields and only if not PII)
     */
    tm_value?: string
}
